import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode, KeyboardEvent as ReactKeyboardEvent } from 'react';

export interface MenuItem {
  id: string;
  label: string;
  hint?: string | null;
  disabled?: boolean;
  onSelect(): void;
}

/** Accessible dropdown menu (button + role=menu, arrow keys, Escape, type-ahead-free). */
export function Menu({
  label,
  buttonContent,
  items,
  align = 'end',
  className,
}: {
  label: string;
  buttonContent: ReactNode;
  items: readonly MenuItem[];
  align?: 'start' | 'end';
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const btn = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const first = list.current?.querySelector<HTMLElement>(
      '[role="menuitem"]:not([aria-disabled="true"])',
    );
    first?.focus();
    const onDoc = (e: PointerEvent) => {
      if (!list.current?.contains(e.target as Node) && !btn.current?.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener('pointerdown', onDoc);
    return () => document.removeEventListener('pointerdown', onDoc);
  }, [open]);

  const onKey = (e: ReactKeyboardEvent) => {
    const els = [...(list.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    const idx = els.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next = els[(idx + (e.key === 'ArrowDown' ? 1 : -1) + els.length) % els.length];
      next?.focus();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      btn.current?.focus();
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <div className={`menu ${className ?? ''}`}>
      <button
        ref={btn}
        type="button"
        className="btn btn-ghost"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={id}
        aria-label={label}
        onClick={() => setOpen((o) => !o)}
      >
        {buttonContent}
      </button>
      {open && (
        <div
          ref={list}
          id={id}
          role="menu"
          aria-label={label}
          className={`menu-list menu-${align}`}
          tabIndex={-1}
          onKeyDown={onKey}
        >
          {items.map((it) => (
            <div
              key={it.id}
              role="menuitem"
              tabIndex={-1}
              aria-disabled={it.disabled ? true : undefined}
              className="menu-item"
              onClick={() => {
                if (it.disabled) return;
                setOpen(false);
                it.onSelect();
              }}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && !it.disabled) {
                  e.preventDefault();
                  setOpen(false);
                  it.onSelect();
                }
              }}
            >
              <span>{it.label}</span>
              {it.hint && <kbd>{it.hint}</kbd>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
